<template>
  <div>
    <!-- Cart Overlay -->
    <div
        v-if="cartStore.isOpen"
        class="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        @click="cartStore.toggleCart"
    ></div>

    <!-- Cart Panel -->
    <div
        class="fixed top-0 right-0 h-full w-full sm:w-96 bg-background z-50 shadow-xl transform transition-transform duration-300 ease-in-out overflow-hidden flex flex-col"
        :class="cartStore.isOpen ? 'translate-x-0' : 'translate-x-full'"
    >
      <!-- Cart Header -->
      <div class="bg-background-light p-4 flex items-center justify-between border-b border-background-lighter">
        <h2 class="text-lg font-medium flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          Your Cart
          <span v-if="cartStore.count > 0" class="ml-2 bg-primary text-white rounded-full px-2 py-0.5 text-xs">
            {{ cartStore.count }}
          </span>
        </h2>
        <button
            @click="cartStore.toggleCart"
            class="text-text-secondary hover:text-text p-2 rounded-full hover:bg-background-lighter transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </button>
      </div>

      <!-- Cart Body -->
      <div class="flex-grow overflow-y-auto py-4 px-4">
        <!-- Empty Cart State -->
        <div v-if="cartStore.isEmpty" class="text-center py-12">
          <div class="text-text-secondary mb-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 class="text-lg font-medium mb-2">Your cart is empty</h3>
          <p class="text-text-secondary mb-6">
            Looks like you haven't added any products to your cart yet.
          </p>
          <button
              @click="cartStore.toggleCart"
              class="bg-primary hover:bg-primary-dark text-white py-2 px-6 rounded-md inline-flex items-center transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clip-rule="evenodd" />
            </svg>
            Continue Shopping
          </button>
        </div>

        <!-- Cart Items -->
        <div v-else class="space-y-4">
          <div
              v-for="item in cartStore.items"
              :key="item.id"
              class="bg-background-light rounded-lg overflow-hidden border border-background-lighter"
          >
            <div class="flex">
              <!-- Product Image -->
              <div class="w-24 h-24 bg-background-lighter flex-shrink-0">
                <img
                    :src="item.image || '/placeholder-image.png'"
                    :alt="item.title"
                    class="w-full h-full object-cover"
                    @error="handleImageError"
                >
              </div>

              <!-- Product Details -->
              <div class="flex-grow p-3 flex flex-col justify-between">
                <div>
                  <h4 class="font-medium text-sm">{{ item.title || 'Product Title' }}</h4>
                  <p class="text-primary font-medium text-sm">
                    {{ formatPrice(item.price) }}
                  </p>
                </div>

                <!-- Quantity Controls -->
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <button
                        @click="decrementQuantity(item)"
                        class="w-6 h-6 flex items-center justify-center bg-background hover:bg-background-lighter rounded-full text-text-secondary"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd" />
                      </svg>
                    </button>
                    <span class="mx-2 text-sm w-4 text-center">{{ item.quantity }}</span>
                    <button
                        @click="incrementQuantity(item)"
                        class="w-6 h-6 flex items-center justify-center bg-background hover:bg-background-lighter rounded-full text-text-secondary"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                      </svg>
                    </button>
                  </div>

                  <!-- Remove Item Button -->
                  <button
                      @click="cartStore.removeItem(item.id)"
                      class="text-text-secondary hover:text-red-500 p-1 rounded-full hover:bg-background-lighter transition-colors"
                      aria-label="Remove item"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>

            <!-- Additional Custom Fields Section -->
            <div v-if="hasCustomFields(item)" class="px-3 pb-3 pt-0 text-sm border-t border-background-lighter mt-1">
              <!-- Player Info / Message -->
              <div v-if="item.playerInfo" class="mb-1.5 flex">
                <span class="text-text-secondary font-medium mr-1.5">
                  {{ item.message || 'Player Info:' }}
                </span>
                <span class="font-medium">{{ item.playerInfo }}</span>
              </div>

              <!-- Custom Amount -->
              <div v-if="item.amount" class="mb-1.5 flex justify-between">
                <span class="text-text-secondary font-medium">Amount:</span>
                <span class="font-medium">{{ item.amount }}</span>
              </div>

              <!-- Premium Amount -->
              <div v-if="item.amount2" class="mb-1.5 flex justify-between">
                <span class="text-text-secondary font-medium">Premium Amount:</span>
                <span class="font-medium">{{ item.amount2 }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cart Footer -->
      <div class="border-t border-background-lighter p-4 bg-background-light">
        <!-- Cart Summary -->
        <div v-if="!cartStore.isEmpty" class="mb-4">
          <div class="flex justify-between mb-2">
            <span class="text-text-secondary">Subtotal</span>
            <span class="font-medium">{{ formatPrice(cartStore.total) }}</span>
          </div>
          <div class="flex justify-between mb-2">
            <span class="text-text-secondary">Shipping</span>
            <span>Free</span>
          </div>
          <div class="flex justify-between text-lg font-medium">
            <span>Total</span>
            <span>{{ formatPrice(cartStore.total) }}</span>
          </div>
        </div>

        <!-- Cart Actions -->
        <div class="space-y-3">
          <button
              v-if="!cartStore.isEmpty"
              class="w-full bg-primary hover:bg-primary-dark text-white py-3 rounded-md font-medium transition-colors flex items-center justify-center"
              @click="checkout"
          >
            Proceed to Checkout
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
          <button
              @click="cartStore.toggleCart"
              class="w-full border border-background-lighter hover:bg-background-lighter py-2 rounded-md transition-colors"
          >
            Continue Shopping
          </button>
        </div>

        <div class="flex items-center mt-4 text-xs text-text-secondary justify-center">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Secure checkout
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useCartStore } from '../stores/cartStore';
import { useDataStore } from '@/stores/dataStore.js'
import { useAuthStore } from '../stores/auth';
import { useRouter } from 'vue-router';
import {onMounted, watch} from 'vue';

export default {
  name: 'Cart',
  setup() {
    const cartStore = useCartStore();
    const dataStore = useDataStore();
    const authStore = useAuthStore();
    const router = useRouter();


    // Format price based on user's currency preference
    const formatPrice = (price) => {
      if (dataStore.shouldHidePrices) {
        return '***';
      }

      const symbol = dataStore.currencySymbol;
      const convertedPrice = dataStore.convertPrice(price);

      return `${symbol}${convertedPrice}`;
    };

    // Check if an item has any custom fields to display
    const hasCustomFields = (item) => {
      return item.playerInfo || item.amount || item.amount2;
    };

    const handleImageError = (event) => {
      event.target.src = '/placeholder-image.png';
    };

    const incrementQuantity = (item) => {
      cartStore.updateQuantity(item.id, item.quantity + 1);
    };

    const decrementQuantity = (item) => {
      if (item.quantity > 1) {
        cartStore.updateQuantity(item.id, item.quantity - 1);
      } else {
        cartStore.removeItem(item.id);
      }
    };

    const checkout = () => {
      // Save cart before navigation
      localStorage.setItem('eskragCart', JSON.stringify(cartStore.items));

      // Close the cart panel
      cartStore.toggleCart();

      // Use setTimeout to ensure the cart is closed before navigation
      setTimeout(() => {
        // Navigate to checkout page
        router.push('/checkout');
      }, 100);
    };
    watch(() => cartStore.isOpen, (isOpen) => {
      if (isOpen && dataStore.isDataReady) {
        cartStore.recalculatePrices(dataStore);
      }
    });

    // Recalculate prices when dataStore becomes ready
    watch(() => dataStore.isDataReady, (isReady) => {
      if (isReady) {
        cartStore.recalculatePrices(dataStore);
      }
    });
    onMounted(() => {
   });

    return {
      cartStore,
      dataStore,
      formatPrice,
      hasCustomFields,
      handleImageError,
      incrementQuantity,
      decrementQuantity,
      checkout
    };
  }
}
</script>